import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  Checkbox,
  IconButton,
  Typography,
  Button,
  Menu,
  MenuItem,
  ListItemText,
  Popper,
  ClickAwayListener,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AddIcon from "@mui/icons-material/Add";
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import { Chip } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { Dayjs } from "dayjs";
import LeadsTable from "../Components/LeadsTable";
import KeyboardDoubleArrowUpRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowUpRounded';

const ShortlistedLeadsTable = ({
  leads,
  totalProfiles,
  selectedJobTitles,
  selectedLeadStatus,
  selectedLeadPotential,
  setSelectedJobTitles,
  setSelectedLeadStatus,
  setSelectedLeadPotential,
  currentPage,
  setCurrentPage,
  onLeadClick,
  selectedUsernames,
  setSelectedUsernames,
}: any) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [tempDate, setTempDate] = useState<Dayjs | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const calendarAnchorRef = useRef<HTMLDivElement | null>(null);

  const handleCalendarApply = useCallback(() => {
    setSelectedDate(tempDate);
    setCalendarOpen(false);
  }, [tempDate]);

  const handleCalendarCancel = useCallback(() => {
    setTempDate(selectedDate);
    setCalendarOpen(false);
  }, [selectedDate]);

  const DropdownMultiSelect = React.memo(({
    label,
    searchable = false,
    value,
    onChange,
    options,
  }: {
    label: string;
    searchable?: boolean;
    value: string[];
    onChange: (val: string[]) => void;
    options: string[];
  }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const firstMatchRef = useRef<HTMLLIElement | null>(null);

    useEffect(() => {
      if (firstMatchRef.current) {
        firstMatchRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }, [searchTerm]);

    const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
      setAnchorEl(null);
      setSearchTerm("");
    }, []);

    const handleToggleOption = useCallback((option: string) => {
      const updated = value.includes(option)
        ? value.filter((item) => item !== option)
        : [...value, option];
      onChange(updated);
    }, [value, onChange]);

    const filteredOptions = useMemo(() => 
      options.filter((option: string) =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [options, searchTerm]);

    return (
      <>
        <Button
          variant="outlined"
          endIcon={<KeyboardArrowDownIcon />}
          size="small"
          onClick={handleClick}
          sx={{
            textTransform: "none",
            color: "#5f6368",
            borderColor: "#D1D5DB",
            background: "#FAFAFA",
            borderRadius: "8px",
            width: 200,
            justifyContent: "space-between",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "12px",
          }}
        >
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block",
              flexGrow: 1,
            }}
          >
            <Typography
              noWrap
              sx={{ fontSize: "12px", color: "#5f6368", textAlign: "left" }}
            >
              {selectedOptions.length > 0 ? selectedOptions.join(", ") : label}
            </Typography>
          </Box>
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            style: {
              width: 200,
              maxHeight: 200,
              overflowY: "auto",
              marginTop: "10px",
              border: "1px solid #D1D5DB",
            },
            sx: {
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": { background: "#ffffff" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#DFE2E7",
                borderRadius: "3px",
              },
            },
          }}
          disableScrollLock
        >
          {searchable && (
            <Box px={1} py={1}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search with keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: "#f5f5f5",
                    borderRadius: "6px",
                    fontSize: "12px",
                  },
                }}
              />
            </Box>
          )}
        </Menu>
      </>
    );
  });

  const memoizedLeads = useMemo(() => leads, [leads]);

  return (
    <Box sx={{ padding: "10px 15px", bgcolor: "#fff" }}>
      <Box
        sx={{ display: "flex", gap: 2, mb: 2, justifyContent: "space-between" }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            placeholder="Search with keywords..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            sx={{ width: 250 }}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
        {selectedJobTitles.map((title: string) => (
          <Chip
            key={`job-${title}`}
            label={`Job Title: ${title}`}
            onDelete={() =>
              setSelectedJobTitles((prev: string[]) =>
                prev.filter((item) => item !== title)
              )
            }
            deleteIcon={<CloseIcon />}
            sx={{
              bgcolor: "#EAF1FF",
              color: "#2563EB",
              "& .MuiChip-deleteIcon": { color: "#2563EB" },
              fontWeight: 600,
            }}
          />
        ))}
        {selectedLeadStatus.map((status: string) => (
          <Chip
            key={`status-${status}`}
            label={`Batch: ${status}`}
            onDelete={() =>
              setSelectedLeadStatus((prev: string[]) =>
                prev.filter((item) => item !== status)
              )
            }
            deleteIcon={<CloseIcon />}
            sx={{
              bgcolor: "#EAF1FF",
              color: "#2563EB",
              "& .MuiChip-deleteIcon": { color: "#2563EB" },
              fontWeight: 600,
            }}
          />
        ))}
        {selectedLeadPotential.map((potential: string) => (
          <Chip
            key={`potential-${potential}`}
            label={`Score: ${potential}`}
            onDelete={() =>
              setSelectedLeadPotential((prev: string[]) =>
                prev.filter((item) => item !== potential)
              )
            }
            deleteIcon={<CloseIcon />}
            sx={{
              bgcolor: "#EAF1FF",
              color: "#2563EB",
              "& .MuiChip-deleteIcon": { color: "#2563EB" },
              fontWeight: 600,
            }}
          />
        ))}
      </Box>

      <LeadsTable
        leads={memoizedLeads}
        showCheckbox={false}
        columns={[
          {
            id: 'name',
            label: 'Name',
            render: useCallback((lead) => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {lead.black_list && (
                  <Tooltip title="Blacklisted Candidate" arrow>
                    <NoAccountsIcon fontSize="small" sx={{color: '#000' }} />
                  </Tooltip>
                )}
                {lead.priority_list && (
                  <Tooltip title="Prioritize User" arrow>
                    <KeyboardDoubleArrowUpRoundedIcon fontSize="small" sx={{color: '#000' }} />
                  </Tooltip>
                )}
                <div
                  style={{ cursor: "pointer", color: "#5175e0", fontWeight: 600 }}
                  onClick={() => onLeadClick({
                    ...lead,
                    campaignId: lead.lead_info?.lead_id
                  })}
                >
                  {lead.name}
                </div>
              </Box>
            ), [onLeadClick]),
          },
          {
            id: 'jobTitle',
            label: 'Job Title',
            render: (lead) => (
              <Tooltip title={lead.position} arrow>
                <span>
                  {lead.position?.length > 30
                    ? `${lead.position.substring(0, 30)}...`
                    : lead.position}
                </span>
              </Tooltip>
            ),
          },
          {
            id: 'email',
            label: 'Email',
            render: (lead) => lead.email || "N/A",
          },
          {
            id: 'companyName',
            label: 'Company Name',
            render: (lead) => lead.current_company_name,
          },
          {
            id: 'location',
            label: 'Location',
            render: (lead) => 
              `${lead.location}, ${lead.country_code}` ||
              `${lead.city}, ${lead.country_code}`,
              
          },
          {
            id: 'industry',
            label: 'Industry',
            render: (lead) => lead.industry || "N/A",
          },
        ]}
        totalProfiles={totalProfiles}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        selectedUsernames={selectedUsernames}
        onSelectLead={useCallback((username: string, checked: boolean) => {
          setSelectedUsernames((prev: string[]) =>
            checked
              ? [...prev, username]
              : prev.filter((name: string) => name !== username)
          );
        }, [])}
        onSelectAll={useCallback((checked: boolean) => {
          const usernames = leads.map((lead: { username: string }) => lead.username);
          setSelectedUsernames(checked ? usernames : []);
        }, [leads])}
      />
    </Box>
  );
};

export default React.memo(ShortlistedLeadsTable);
